import {
  Button,
  Card,
  Checkbox,
  MARKER_ICON_GROUPS,
  isGlyphAvailable,
  useToast,
} from '@citizen-walk/shared-ui';
import {
  MODERN_COLOR_PALETTE,
  POST_TYPE_FIELD_KEY,
  defaultColorRule,
  defaultQuadrantColorRule,
  isValidColorRuleExpr,
  parseColorRule,
  syncColorRuleBucketsFromField,
  type ColorRule,
  type ColorRuleBucket,
  type ColorRuleQuadrantCell,
  type ColorRuleValue,
  type ColorRuleQuadrant,
  type District,
  type FieldDef,
} from '@citizen-walk/shared-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { ColorSwatch } from '../masters';

/**
 * 地区別 ColorRule 編集パネル (Sprint BG-DataSettings 6+7、GAP-A4)。
 *
 * 旧 admin `old/apps/admin/src/pages/DistrictDetailPage.tsx` L1800-1990 の colorRule
 * editor を v2 風に再構成して移植。L17 (旧 v1 詳細 panel 完全踏襲) の達成:
 *   - **value 形式** (1 field × N bucket): postFields から fieldKey を選ぶ → buckets 配列を編集
 *   - **quadrant 形式** (2 field × 4 cell): xKey / yKey を選ぶ → cells 配列を編集
 *   - 各 row で MODERN_COLOR_PALETTE picker + label + expr (構文 validate 付き)
 *   - "syncFromDataSettings" ボタン: 選択中の fieldKey の options[] から buckets[] を再生成
 *     (旧 v1 「options 連動」自動採色)
 *   - fallbackColor / fallbackLabel も編集可
 *
 * **`District.mapDisplay.colorRule` の唯一の編集 UI** (2026-09-03)。
 * 以前は旧 `DistrictDisplayConfigPanel` が同じフィールドに `AnalysisColorRule` (colorAxis 形式) を
 * 保存しており、保存すると本 panel の設定 (kind: value/quadrant) を上書きして消していた。
 * colorAxis 形式は user アプリに読み手がゼロ (useColorRule / ColorBadge が明示的に無視) で
 * 機能として成立していなかったため、旧 panel は撤去した (stg / prod とも旧形式データは 0 件)。
 *
 * 認可: firestore.rules で master のみ write 可能。UI でも `canEdit` で gate。
 */

interface RowState extends ColorRuleBucket {
  exprError?: string;
}

interface CellState extends ColorRuleQuadrantCell {
  xExprError?: string;
  yExprError?: string;
}

interface FormState {
  kind: 'value' | 'quadrant';
  fieldKey: string;
  buckets: RowState[];
  xKey: string;
  yKey: string;
  cells: CellState[];
  fallbackColor: string;
  fallbackLabel: string;
}

/** pure: District → form state (旧 v1 panel state 初期化 logic と等価)。 */
export function draftFromDistrict(d: District): FormState {
  const parsed = parseColorRule(d.mapDisplay?.colorRule);
  const postFieldsKeys = d.dataSettings?.postFields?.map((f) => f.key) ?? [];
  const fallbackKey = postFieldsKeys[0] ?? POST_TYPE_FIELD_KEY;

  if (parsed && parsed.kind === 'value') {
    return {
      kind: 'value',
      fieldKey: parsed.fieldKey,
      buckets: parsed.buckets.map((b) => ({ ...b })),
      xKey: postFieldsKeys[0] ?? '',
      yKey: postFieldsKeys[1] ?? '',
      cells: defaultQuadrantColorRule(postFieldsKeys[0] ?? '', postFieldsKeys[1] ?? '').cells.map(
        (c) => ({ ...c }),
      ),
      fallbackColor: parsed.fallbackColor ?? '#9CA3AF',
      fallbackLabel: parsed.fallbackLabel ?? '—',
    };
  }
  if (parsed && parsed.kind === 'quadrant') {
    return {
      kind: 'quadrant',
      fieldKey: fallbackKey,
      buckets: [],
      xKey: parsed.xKey,
      yKey: parsed.yKey,
      cells: parsed.cells.map((c) => ({ ...c })),
      fallbackColor: parsed.fallbackColor ?? '#9CA3AF',
      fallbackLabel: parsed.fallbackLabel ?? 'ニュートラル',
    };
  }
  // 未設定 — value 形式 + 空 buckets
  return {
    kind: 'value',
    fieldKey: fallbackKey,
    buckets: [],
    xKey: postFieldsKeys[0] ?? '',
    yKey: postFieldsKeys[1] ?? '',
    cells: [],
    fallbackColor: '#9CA3AF',
    fallbackLabel: '—',
  };
}

/** pure: form state → ColorRule payload + 検証エラー一覧。 */
export function buildColorRulePayload(state: FormState): {
  rule: ColorRule | null;
  errors: string[];
} {
  const errors: string[] = [];
  if (state.kind === 'value') {
    if (!state.fieldKey.trim()) {
      errors.push('value 形式: fieldKey 必須');
    }
    if (state.buckets.length === 0) {
      errors.push('value 形式: buckets を 1 件以上指定してください');
    }
    const buckets: ColorRuleBucket[] = [];
    for (const [i, b] of state.buckets.entries()) {
      const label = b.label.trim();
      const expr = b.expr;
      const color = b.color.trim();
      if (!label) errors.push(`bucket #${i + 1}: label 必須`);
      if (!color) errors.push(`bucket #${i + 1}: color 必須`);
      if (!isValidColorRuleExpr(expr)) errors.push(`bucket #${i + 1}: expr 構文エラー (${expr})`);
      if (label && color) {
        const out: ColorRuleBucket = { label, expr, color };
        if (b.icon?.trim()) out.icon = b.icon.trim();
        buckets.push(out);
      }
    }
    if (errors.length > 0) return { rule: null, errors };
    const rule: ColorRuleValue = {
      kind: 'value',
      fieldKey: state.fieldKey.trim(),
      buckets,
      fallbackColor: state.fallbackColor.trim() || '#9CA3AF',
      fallbackLabel: state.fallbackLabel.trim() || '—',
    };
    return { rule, errors };
  }
  // quadrant
  if (!state.xKey.trim() || !state.yKey.trim()) {
    errors.push('quadrant 形式: xKey と yKey 両方必須');
  }
  if (state.cells.length === 0) {
    errors.push('quadrant 形式: cells を 1 件以上指定してください');
  }
  const cells: ColorRuleQuadrantCell[] = [];
  for (const [i, c] of state.cells.entries()) {
    const label = c.label.trim();
    const xExpr = c.xExpr;
    const yExpr = c.yExpr;
    const color = c.color.trim();
    if (!label) errors.push(`cell #${i + 1}: label 必須`);
    if (!color) errors.push(`cell #${i + 1}: color 必須`);
    if (!isValidColorRuleExpr(xExpr)) errors.push(`cell #${i + 1}: xExpr 構文エラー (${xExpr})`);
    if (!isValidColorRuleExpr(yExpr)) errors.push(`cell #${i + 1}: yExpr 構文エラー (${yExpr})`);
    if (label && color) {
      const out: ColorRuleQuadrantCell = { label, xExpr, yExpr, color };
      if (c.icon?.trim()) out.icon = c.icon.trim();
      cells.push(out);
    }
  }
  if (errors.length > 0) return { rule: null, errors };
  const rule: ColorRuleQuadrant = {
    kind: 'quadrant',
    xKey: state.xKey.trim(),
    yKey: state.yKey.trim(),
    cells,
    fallbackColor: state.fallbackColor.trim() || '#9CA3AF',
    fallbackLabel: state.fallbackLabel.trim() || 'ニュートラル',
  };
  return { rule, errors };
}

export interface DistrictColorRulePanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictColorRulePanel({ district, uid, canEdit }: DistrictColorRulePanelProps) {
  const { toast } = useToast();
  const [state, setState] = useState<FormState>(() => draftFromDistrict(district));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // 投稿マーカーの円サイズ / アイコン記号サイズ (mapDisplay、地区管理者も編集可)。
  const initMarkerSize = district.mapDisplay?.markerSize ?? 10;
  const initIconSize = district.mapDisplay?.iconSize ?? 16;
  const [markerSize, setMarkerSize] = useState<number>(initMarkerSize);
  const [iconSize, setIconSize] = useState<number>(initIconSize);
  const sizesDirty = markerSize !== initMarkerSize || iconSize !== initIconSize;
  async function saveSizes() {
    if (!canEdit) return;
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        { mapDisplay: { markerSize, iconSize }, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', 'マーカーサイズを保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // 地図手書きメモ機能の表示有無 (docs/24、mapDisplay.memoEnabled)。トグルは即時保存。
  // 既存の mapDisplay field-scoped rule (master + 地区管理者) をそのまま再利用するため rule 変更不要。
  const initMemoEnabled = district.mapDisplay?.memoEnabled ?? false;
  const [memoEnabled, setMemoEnabled] = useState<boolean>(initMemoEnabled);
  async function saveMemoEnabled(next: boolean) {
    if (!canEdit) return;
    setMemoEnabled(next); // optimistic
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        { mapDisplay: { memoEnabled: next }, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', next ? '手書きメモを有効にしました' : '手書きメモを無効にしました');
    } catch (e) {
      setMemoEnabled(!next); // revert
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  // 地図クラスタ(集約)表示の有無 (mapDisplay.clusterEnabled、旧アプリの地区設定を復活)。既定 OFF。
  const initClusterEnabled = district.mapDisplay?.clusterEnabled ?? false;
  const [clusterEnabled, setClusterEnabled] = useState<boolean>(initClusterEnabled);
  async function saveClusterEnabled(next: boolean) {
    if (!canEdit) return;
    setClusterEnabled(next); // optimistic
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'districts', district.id),
        { mapDisplay: { clusterEnabled: next }, updatedAt: serverTimestamp(), updatedBy: uid },
        { merge: true },
      );
      toast('success', next ? 'クラスタ表示を有効にしました' : 'クラスタ表示を無効にしました');
    } catch (e) {
      setClusterEnabled(!next); // revert
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  const postFields: FieldDef[] = useMemo(
    () => district.dataSettings?.postFields ?? [],
    [district.dataSettings?.postFields],
  );
  const postFieldKeys = useMemo(() => postFields.map((f) => f.key), [postFields]);

  function setKind(kind: 'value' | 'quadrant') {
    setState((prev) => {
      if (kind === prev.kind) return prev;
      if (kind === 'quadrant' && prev.cells.length === 0) {
        const def = defaultQuadrantColorRule(
          prev.xKey || postFieldKeys[0] || '',
          prev.yKey || postFieldKeys[1] || '',
        );
        return {
          ...prev,
          kind,
          cells: def.cells.map((c) => ({ ...c })),
          fallbackLabel: 'ニュートラル',
        };
      }
      if (kind === 'value' && prev.buckets.length === 0 && !prev.fieldKey) {
        return {
          ...prev,
          kind,
          fieldKey: postFieldKeys[0] ?? POST_TYPE_FIELD_KEY,
          fallbackLabel: '—',
        };
      }
      return { ...prev, kind };
    });
  }

  function addBucket() {
    setState((prev) => {
      const usedColors = prev.buckets.map((b) => b.color.toLowerCase());
      const next = (MODERN_COLOR_PALETTE as readonly string[]).find(
        (c) => !usedColors.includes(c.toLowerCase()),
      );
      return {
        ...prev,
        buckets: [...prev.buckets, { label: '', expr: '', color: next ?? '#3B82F6' }],
      };
    });
  }

  function updateBucket(idx: number, patch: Partial<RowState>) {
    setState((prev) => ({
      ...prev,
      buckets: prev.buckets.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));
  }

  function removeBucket(idx: number) {
    setState((prev) => ({
      ...prev,
      buckets: prev.buckets.filter((_, i) => i !== idx),
    }));
  }

  function addCell() {
    setState((prev) => {
      const usedColors = prev.cells.map((c) => c.color.toLowerCase());
      const next = (MODERN_COLOR_PALETTE as readonly string[]).find(
        (c) => !usedColors.includes(c.toLowerCase()),
      );
      return {
        ...prev,
        cells: [...prev.cells, { label: '', xExpr: '', yExpr: '', color: next ?? '#3B82F6' }],
      };
    });
  }

  function updateCell(idx: number, patch: Partial<CellState>) {
    setState((prev) => ({
      ...prev,
      cells: prev.cells.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));
  }

  function removeCell(idx: number) {
    setState((prev) => ({
      ...prev,
      cells: prev.cells.filter((_, i) => i !== idx),
    }));
  }

  function handleSyncFromDataSettings() {
    if (state.kind !== 'value') {
      toast('error', 'value 形式でのみ利用可能');
      return;
    }
    const field = postFields.find((f) => f.key === state.fieldKey);
    if (!field) {
      toast('error', `postFields に key='${state.fieldKey}' がありません`);
      return;
    }
    if (!field.options || field.options.length === 0) {
      toast('error', `field '${field.key}' に options[] がありません (sync 不可)`);
      return;
    }
    const currentRule: ColorRule = {
      kind: 'value',
      fieldKey: state.fieldKey,
      buckets: state.buckets.map((b) => ({ ...b })),
    };
    const synced = syncColorRuleBucketsFromField(field, currentRule);
    setState((prev) => ({ ...prev, buckets: synced.buckets.map((b) => ({ ...b })) }));
    toast('success', `${synced.buckets.length} 件の bucket を sync しました`);
  }

  async function handleSave() {
    if (!canEdit) return;
    const { rule, errors: errs } = buildColorRulePayload(state);
    setErrors(errs);
    if (errs.length > 0 || !rule) {
      toast('error', `入力エラー (${errs.length} 件)`);
      return;
    }
    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          mapDisplay: { colorRule: rule },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'ColorRule を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    if (!canEdit) return;
    if (
      !confirm(
        '地区別 ColorRule をクリアしますか? (user 側は M5 displayRules.colorRule にフォールバック)',
      )
    ) {
      return;
    }
    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          mapDisplay: { colorRule: null },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'ColorRule をクリアしました');
      const empty = defaultColorRule(state.fieldKey);
      setState((prev) => ({
        ...prev,
        kind: 'value',
        buckets: empty.buckets.map((b) => ({ ...b })),
      }));
    } catch (e) {
      toast('error', `クリア失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-color-rule-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-bold text-slate-900">
          🎨 マーカー色ルール (旧 v1 ColorRule、Sprint BG 6+7)
        </h3>
        {district.mapDisplay?.colorRule && (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void handleClear()}
            disabled={!canEdit || saving}
          >
            clear
          </Button>
        )}
      </div>
      <div className="mb-3 grid grid-cols-1 gap-1 text-xs text-slate-500">
        <p>
          投稿ピンの色と地図の凡例を決めます。1 軸は 1 つの項目の値で色を分け、2 軸は 2
          つの項目の正負で 4 通りに色を分けます。
        </p>
        {/* この設定がどこに効くかを明示する。分析ツール側でも使えることが分かりにくいため。 */}
        <p className="text-slate-600">
          ここで決めた色は<strong>投稿アプリ</strong>（地図・一覧・投稿の詳細）と
          <strong>分析ツールの地図</strong>
          （ピン・凡例・ヒートマップ）の両方で使われます。分析ツールの地図は、ここで色を決めておくと
          <strong>投稿アプリと同じ色で開きます</strong>
          。分析ツール側で別の色分けを見たいときは、地図の「色分け基準」から切り替えられます。
        </p>
        <p>未設定のままなら、投稿の内容から自動で決まる色分けになります。</p>
      </div>

      {/* マーカー表示サイズ (mapDisplay.markerSize / iconSize)。地区管理者も編集可。 */}
      <div className="mb-4 grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold text-slate-700">マーカーサイズ</p>
        <SizeRow
          label="円の大きさ"
          value={markerSize}
          min={4}
          max={30}
          disabled={!canEdit || saving}
          onChange={setMarkerSize}
        />
        <SizeRow
          label="アイコン記号の大きさ"
          value={iconSize}
          min={8}
          max={40}
          disabled={!canEdit || saving}
          onChange={setIconSize}
        />
        <div className="flex items-center justify-end gap-2">
          {sizesDirty && <span className="text-[11px] text-amber-600">未保存</span>}
          <Button
            size="sm"
            variant="secondary"
            disabled={!canEdit || saving || !sizesDirty}
            onClick={() => void saveSizes()}
          >
            サイズを保存
          </Button>
        </div>
      </div>

      {/* 地図手書きメモ機能の ON/OFF (docs/24)。ユーザーアプリの地図に手書きメモのモードを出すか。
          即時保存。master + 地区管理者が切替可 (mapDisplay の field-scoped rule)。 */}
      <div className="mb-4 grid grid-cols-1 gap-1 rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold text-slate-700">手書きメモ機能</p>
        <Checkbox
          label="ユーザーアプリの地図で手書きメモを有効にする"
          checked={memoEnabled}
          onChange={(c) => void saveMemoEnabled(c)}
          disabled={!canEdit || saving}
        />
        <p className="text-[11px] text-slate-500">
          ONにすると、ユーザーの地図右上(拡大/方位ボタンの並び)に手書きメモの切替ボタンが表示されます。
        </p>
      </div>

      {/* 地図クラスタ(集約)表示の ON/OFF (旧アプリの地区設定を復活、mapDisplay.clusterEnabled)。既定 OFF。 */}
      <div className="mb-4 grid grid-cols-1 gap-1 rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="text-xs font-semibold text-slate-700">地図クラスタ表示</p>
        <Checkbox
          label="投稿が多い場合にクラスタ（集約表示）を有効にする"
          checked={clusterEnabled}
          onChange={(c) => void saveClusterEnabled(c)}
          disabled={!canEdit || saving}
        />
      </div>

      <section className="mb-4">
        <label className="flex items-center gap-3 text-sm mb-3">
          <span className="text-xs text-slate-500">形式</span>
          <select
            value={state.kind}
            onChange={(e) => setKind(e.target.value as 'value' | 'quadrant')}
            disabled={!canEdit || saving}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            data-testid="kind-select"
          >
            <option value="value">value (1 field × N bucket)</option>
            <option value="quadrant">quadrant (2 field × 4 cell)</option>
          </select>
        </label>

        {state.kind === 'value' ? (
          <div className="grid grid-cols-1 gap-3">
            <label className="grid grid-cols-1 gap-1">
              <span className="text-xs text-slate-500">fieldKey</span>
              <div className="flex items-center gap-2">
                <select
                  value={postFieldKeys.includes(state.fieldKey) ? state.fieldKey : '__custom__'}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v !== '__custom__') {
                      setState((p) => ({ ...p, fieldKey: v }));
                    }
                  }}
                  disabled={!canEdit || saving}
                  className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
                  data-testid="fieldkey-select"
                >
                  {postFieldKeys.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                  <option value="__custom__">— カスタム入力 —</option>
                </select>
                <input
                  type="text"
                  value={state.fieldKey}
                  onChange={(e) => setState((p) => ({ ...p, fieldKey: e.target.value }))}
                  disabled={!canEdit || saving}
                  className="flex-1 min-w-0 min-h-touch px-2 border border-slate-300 rounded text-xs font-mono"
                  placeholder="例: category"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={handleSyncFromDataSettings}
                  disabled={!canEdit || saving}
                  data-testid="sync-from-data-settings"
                >
                  options から sync
                </Button>
              </div>
            </label>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-slate-600 uppercase">buckets</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={addBucket}
                  disabled={!canEdit || saving}
                  data-testid="add-bucket"
                >
                  + 行追加
                </Button>
              </div>
              <ul className="grid grid-cols-1 gap-2">
                {state.buckets.map((b, idx) => {
                  const exprValid = isValidColorRuleExpr(b.expr);
                  return (
                    <li
                      key={idx}
                      className="flex flex-wrap items-center gap-2"
                      data-testid={`bucket-row-${idx}`}
                    >
                      <ColorSwatch
                        value={b.color || '#3B82F6'}
                        onChange={(c) => updateBucket(idx, { color: c ?? '#3B82F6' })}
                        ariaLabel={`bucket ${idx + 1} 色`}
                      />
                      <input
                        type="text"
                        value={b.label}
                        onChange={(e) => updateBucket(idx, { label: e.target.value })}
                        disabled={!canEdit || saving}
                        className="flex-1 min-w-[120px] min-h-touch px-2 border border-slate-300 rounded text-xs"
                        placeholder="label (例: 💚 ホッと)"
                      />
                      <input
                        type="text"
                        value={b.expr}
                        onChange={(e) => updateBucket(idx, { expr: e.target.value })}
                        disabled={!canEdit || saving}
                        className={`flex-1 min-w-[120px] min-h-touch px-2 border rounded text-xs font-mono ${
                          exprValid ? 'border-slate-300' : 'border-danger-500'
                        }`}
                        placeholder="expr (例: ==danger / >5)"
                      />
                      <IconPicker
                        value={b.icon ?? ''}
                        color={b.color || '#3B82F6'}
                        disabled={!canEdit || saving}
                        onChange={(ic) => updateBucket(idx, { icon: ic })}
                      />
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => removeBucket(idx)}
                        disabled={!canEdit || saving}
                      >
                        削除
                      </Button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {/* 2 軸 (quadrant) の軸も、1 軸 (value) の fieldKey と同じく投稿項目から選べるようにする。
                自由入力だけだと綴りを間違えても気づけず、色が一切付かない状態になるため。 */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <AxisKeyField
                label="横軸に使う投稿項目 (xKey)"
                value={state.xKey}
                onChange={(v) => setState((p) => ({ ...p, xKey: v }))}
                postFieldKeys={postFieldKeys}
                disabled={!canEdit || saving}
                testId="xkey"
                placeholder="例: HappinessScore"
              />
              <AxisKeyField
                label="縦軸に使う投稿項目 (yKey)"
                value={state.yKey}
                onChange={(v) => setState((p) => ({ ...p, yKey: v }))}
                postFieldKeys={postFieldKeys}
                disabled={!canEdit || saving}
                testId="ykey"
                placeholder="例: HealthScore"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-bold text-slate-600 uppercase">cells</span>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={addCell}
                  disabled={!canEdit || saving}
                  data-testid="add-cell"
                >
                  + 行追加
                </Button>
              </div>
              <ul className="grid grid-cols-1 gap-2">
                {state.cells.map((c, idx) => (
                  <li
                    key={idx}
                    className="flex flex-wrap items-center gap-2"
                    data-testid={`cell-row-${idx}`}
                  >
                    <ColorSwatch
                      value={c.color || '#3B82F6'}
                      onChange={(v) => updateCell(idx, { color: v ?? '#3B82F6' })}
                      ariaLabel={`cell ${idx + 1} 色`}
                    />
                    <input
                      type="text"
                      value={c.label}
                      onChange={(e) => updateCell(idx, { label: e.target.value })}
                      disabled={!canEdit || saving}
                      className="flex-1 min-w-[120px] min-h-touch px-2 border border-slate-300 rounded text-xs"
                      placeholder="label"
                    />
                    <input
                      type="text"
                      value={c.xExpr}
                      onChange={(e) => updateCell(idx, { xExpr: e.target.value })}
                      disabled={!canEdit || saving}
                      className="w-24 min-h-touch px-2 border border-slate-300 rounded text-xs font-mono"
                      placeholder="xExpr (>0)"
                    />
                    <input
                      type="text"
                      value={c.yExpr}
                      onChange={(e) => updateCell(idx, { yExpr: e.target.value })}
                      disabled={!canEdit || saving}
                      className="w-24 min-h-touch px-2 border border-slate-300 rounded text-xs font-mono"
                      placeholder="yExpr (>0)"
                    />
                    <IconPicker
                      value={c.icon ?? ''}
                      color={c.color || '#3B82F6'}
                      disabled={!canEdit || saving}
                      onChange={(ic) => updateCell(idx, { icon: ic })}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => removeCell(idx)}
                      disabled={!canEdit || saving}
                    >
                      削除
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </section>

      <section className="grid grid-cols-2 gap-2 mb-3">
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">fallbackColor</span>
          <div className="flex items-center gap-2">
            <ColorSwatch
              value={state.fallbackColor}
              onChange={(c) => setState((p) => ({ ...p, fallbackColor: c ?? '#9CA3AF' }))}
              ariaLabel="fallback 色"
            />
            <input
              type="text"
              value={state.fallbackColor}
              onChange={(e) => setState((p) => ({ ...p, fallbackColor: e.target.value }))}
              disabled={!canEdit || saving}
              className="flex-1 min-h-touch px-2 border border-slate-300 rounded text-xs font-mono"
            />
          </div>
        </label>
        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">fallbackLabel</span>
          <input
            type="text"
            value={state.fallbackLabel}
            onChange={(e) => setState((p) => ({ ...p, fallbackLabel: e.target.value }))}
            disabled={!canEdit || saving}
            className="min-h-touch px-2 border border-slate-300 rounded text-xs"
          />
        </label>
      </section>

      {errors.length > 0 && (
        <ul className="mb-3 text-xs text-danger-700 list-disc pl-5" data-testid="error-list">
          {errors.map((e, i) => (
            <li key={i}>{e}</li>
          ))}
        </ul>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="save-button"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}

/**
 * マーカーアイコン選択 picker。端末フォントに依存せず確実に表示・着色できる図形記号
 * (`MARKER_ICON_CHOICES`) をパレットから選ぶ。色は cell の color で着色されることを示すため
 * パレットも color で表示する。下部に「任意の絵文字」入力も用意するが、絵文字は端末依存
 * (表示されない/色が固定) の注記を出す。
 */
/**
 * 色分けの「軸に使う投稿項目」入力。投稿項目 (dataSettings.postFields) から選ぶ select と、
 * 一覧に無いキーも入れられる自由入力の組。2 軸 (quadrant) の x/y で使う。
 *
 * 自由入力だけだと綴り間違いに気づけず「色が一切付かない」状態になるため、
 * 一覧に無いキーのときは注意書きを出す。
 */
function AxisKeyField({
  label,
  value,
  onChange,
  postFieldKeys,
  disabled,
  testId,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  postFieldKeys: string[];
  disabled: boolean;
  testId: string;
  placeholder: string;
}) {
  const known = postFieldKeys.includes(value);
  return (
    <label className="grid grid-cols-1 gap-1 min-w-0">
      <span className="text-xs text-slate-500">{label}</span>
      <select
        value={known ? value : '__custom__'}
        onChange={(e) => {
          if (e.target.value !== '__custom__') onChange(e.target.value);
        }}
        disabled={disabled}
        className="min-h-touch min-w-0 px-3 border border-slate-300 rounded text-sm bg-white"
        data-testid={`${testId}-select`}
        aria-label={label}
      >
        {postFieldKeys.map((k) => (
          <option key={k} value={k}>
            {k}
          </option>
        ))}
        <option value="__custom__">— 一覧に無いキーを直接入力 —</option>
      </select>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="min-h-touch min-w-0 px-2 border border-slate-300 rounded text-xs font-mono"
        placeholder={placeholder}
        data-testid={`${testId}-input`}
      />
      {value && !known && (
        <span className="text-[11px] text-warning-700">
          この項目名は投稿の入力項目にありません。綴りが違うと色が付きません。
        </span>
      )}
    </label>
  );
}

function IconPicker({
  value,
  color,
  disabled,
  onChange,
}: {
  value: string;
  color: string;
  disabled?: boolean;
  onChange: (icon: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [check, setCheck] = useState<'idle' | 'checking' | 'ok' | 'ng'>('idle');
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);
  // 入力した文字が地図フォント(Noto Sans)に有るかをライブ判定 (debounce 300ms)。
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setCheck('idle');
      return;
    }
    setCheck('checking');
    let cancelled = false;
    const t = setTimeout(() => {
      void isGlyphAvailable(q).then((ok) => {
        if (!cancelled) setCheck(ok ? 'ok' : 'ng');
      });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);
  const pick = (ic: string) => {
    onChange(ic);
    setOpen(false);
    setQuery('');
    setCheck('idle');
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-label="マーカーアイコンを選択"
        title="マーカーアイコンを選択"
        className="w-12 min-h-touch px-1 border border-slate-300 rounded text-base leading-none text-center disabled:opacity-50"
        style={value ? { color } : undefined}
      >
        {value || '—'}
      </button>
      {open && !disabled && (
        <div className="absolute z-30 left-0 mt-1 w-64 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {/* 入力して表示可否を検索 */}
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="記号を入力して確認"
              className="min-w-0 min-h-touch flex-1 rounded border border-slate-300 px-2 text-sm"
            />
            <Button
              size="sm"
              variant="secondary"
              disabled={check !== 'ok'}
              onClick={() => pick(query.trim())}
            >
              使う
            </Button>
          </div>
          <div className="mt-1 min-h-[14px] text-[11px]">
            {check === 'checking' && <span className="text-slate-400">確認中…</span>}
            {check === 'ok' && <span className="text-success-600">✅ 地図に表示できます</span>}
            {check === 'ng' && (
              <span className="text-danger-600">❌ 地図フォント(Noto Sans)に無い文字です</span>
            )}
          </div>

          {/* カテゴリ別パレット (スクロール) */}
          <div className="mt-1.5 max-h-72 overflow-y-auto">
            <div className="mb-1">
              <button
                type="button"
                onClick={() => pick('')}
                title="なし"
                className={`h-8 w-8 rounded text-sm leading-none text-slate-500 hover:bg-slate-100 ${
                  value === '' ? 'ring-2 ring-primary-500' : ''
                }`}
              >
                —
              </button>
            </div>
            {MARKER_ICON_GROUPS.map((g) => (
              <div key={g.label} className="mb-1.5">
                <p className="text-[10px] font-semibold text-slate-500">{g.label}</p>
                <div className="grid grid-cols-7 gap-1">
                  {g.chars.map((ch) => (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => pick(ch)}
                      title={ch}
                      style={{ color }}
                      className={`h-8 rounded text-base leading-none hover:bg-slate-100 ${
                        value === ch ? 'bg-slate-50 ring-2 ring-primary-500' : ''
                      }`}
                    >
                      {ch}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-1 text-[10px] text-slate-400">
            地図フォントに有る記号だけ確実に表示・着色できます。色は選択色で表示。
          </p>
        </div>
      )}
    </div>
  );
}

/** マーカーサイズ調整用のスライダー行。 */
function SizeRow({
  label,
  value,
  min,
  max,
  disabled,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  disabled?: boolean;
  onChange: (v: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs text-slate-600">{label}</span>
      <span className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-32"
        />
        <span className="w-8 text-right text-xs font-mono text-slate-500">{value}</span>
      </span>
    </div>
  );
}
